<template>
  <div>
    <b-input-group class="mb-3">
      <b-form-input
        :id="inputId"
        v-model="localValue"
        ref="dateInput"
        class="form-control"
        type="text"
        :placeholder="placeholder"
        autocomplete="off"
        maxlength="10"
        :class="{ 'is-invalid': isInvalid || isInvalidProps || invalidFeedback }"
        @input="handleInput"
        @keydown="handleKeydown"
        @keyup="handleKeyup"
        @click="updateCursorPosition"
        @focus="updateCursorPosition"
      ></b-form-input>

      <b-input-group-append class="datepicker-append" :class="{ 'is-invalid-append': isInvalid || isInvalidProps || invalidFeedback }">
        <b-form-datepicker
          v-model="datepickerValue"
          button-only
          right
          locale="fr"
          no-flip
          :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
          button-variant="custom"
          menu-class="custom-datepicker-btn"
          aria-controls="inputId"
          v-bind="$attrs"
          :state="!isInvalid"
          @context="onContext"
        ></b-form-datepicker>
      </b-input-group-append>
    </b-input-group>

    <b-form-invalid-feedback v-if="isInvalid" style="display: inline-block;">
      {{ errorMessage }}
    </b-form-invalid-feedback>

    <b-form-invalid-feedback v-else-if="invalidFeedback" style="display: inline-block;">
      {{$t(invalidFeedback)}}
    </b-form-invalid-feedback>
  </div>
</template>

<script>
export default {
  name: "DatepickerInput",
  inheritAttrs: false,
  props: {
    value: String,
    inputId: {
      type: String,
      default: "datepicker-input"
    },
    label: {
      type: String,
      default: "Choisissez une date"
    },
    placeholder: {
      type: String,
      default: "JJ/MM/AAAA"
    },
    isInvalidProps: {
      type: Boolean,
      default: false
    },
    invalidFeedback: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      localValue: this.value ? this.formatDateFR(this.value) : "",
      datepickerValue: this.value || "",
      isInvalid: false,
      errorMessage: "",
      cursorPosition: 0,
      previousValue: "",
      isInternalUpdate: false
    };
  },

  watch: {
    datepickerValue(newValue) {
      if (!newValue && !this.isInternalUpdate) {
        this.localValue = "";
        this.$emit("input", null);
        this.isInvalid = false;
        this.errorMessage = "";
      }
    },
    value(newValue) {
      if (!this.isInternalUpdate) {
        this.localValue = newValue ? this.formatDateFR(newValue) : "";
        this.datepickerValue = newValue || "";
        this.isInvalid = false;
        this.errorMessage = "";
        this.previousValue = this.localValue;
      }
    }
  },

  mounted() {
    this.previousValue = this.localValue;
  },

  methods: {
    updateCursorPosition(event) {
      this.cursorPosition = event.target.selectionStart;
    },

    handleKeydown(event) {
      this.cursorPosition = event.target.selectionStart;

      const allowedKeys = [
        'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
        'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
        'Home', 'End'
      ];

      if (allowedKeys.includes(event.key)) {
        return;
      }

      if (!/^[\d\/]$/.test(event.key)) {
        event.preventDefault();
        return;
      }

      const currentValue = event.target.value;
      const cursorPos = event.target.selectionStart;

      if (event.key === '/') {
        const slashCount = (currentValue.match(/\//g) || []).length;
        if (slashCount >= 2) {
          event.preventDefault();
          return;
        }

        if (cursorPos === 0) {
          event.preventDefault();
          return;
        }
      }

      if (currentValue.length >= 10 && event.target.selectionStart === event.target.selectionEnd) {
        event.preventDefault();
        return;
      }
    },

    handleKeyup(event) {
      this.cursorPosition = event.target.selectionStart;
    },

    handleInput(event) {
      this.isInternalUpdate = true;

      const newValue = event
      const oldValue = this.previousValue;

      const changeInfo = this.detectChange(oldValue, newValue, this.cursorPosition);

      const processedResult = this.processChange(oldValue, changeInfo);

      this.localValue = processedResult.value;
      this.previousValue = processedResult.value;

      this.$nextTick(() => {
        if (this.$refs.dateInput && this.$refs.dateInput.$el) {
          this.$refs.dateInput.$el.setSelectionRange(processedResult.cursorPos, processedResult.cursorPos);
          this.cursorPosition = processedResult.cursorPos;
        }
        this.isInternalUpdate = false;
      });

      this.validateAndEmit(processedResult.value);
    },

    detectChange(oldValue, newValue, cursorPos) {
      if (newValue.length > oldValue.length) {
        let addPos = -1;
        let addedChar = '';

        for (let i = 0; i < newValue.length; i++) {
          if (i >= oldValue.length || oldValue[i] !== newValue[i]) {
            addPos = i;
            addedChar = newValue[i];
            break;
          }
        }

        return {
          type: 'add',
          position: addPos,
          character: addedChar,
          newValue: newValue
        };
      } else if (newValue.length < oldValue.length) {
        return {
          type: 'delete',
          position: cursorPos,
          newValue: newValue
        };
      } else {
        for (let i = 0; i < newValue.length; i++) {
          if (oldValue[i] !== newValue[i]) {
            return {
              type: 'replace',
              position: i,
              character: newValue[i],
              newValue: newValue
            };
          }
        }
      }

      return { type: 'none', newValue: newValue };
    },

    processChange(oldValue, changeInfo) {
      let result = changeInfo.newValue;
      let newCursorPos = this.cursorPosition;

      if (changeInfo.type === 'add') {
        const pos = changeInfo.position;
        const char = changeInfo.character;

        if (/\d/.test(char)) {
          result = this.cleanValue(result);

          if (result.length === 2 && pos === 1) {
            result = result + '/';
            newCursorPos = 3;
          } else if (result.length === 5 && pos === 4) {
            result = result + '/';
            newCursorPos = 6;
          } else {
            newCursorPos = pos + 1;
          }
        } else if (char === '/') {
          result = this.cleanValue(result);
          newCursorPos = pos + 1;
        }

        if (result.length < newCursorPos) {
          newCursorPos = result.length;
        }

      } else if (changeInfo.type === 'delete') {
        result = this.cleanValue(result);
        newCursorPos = changeInfo.position;

      } else if (changeInfo.type === 'replace') {
        const pos = changeInfo.position;
        const char = changeInfo.character;

        result = this.cleanValue(result);
        if (/\d/.test(char)) {
          newCursorPos = pos + 1;
        }
      }

      return {
        value: result,
        cursorPos: Math.min(newCursorPos, result.length)
      };
    },

    cleanValue(value) {
      let cleaned = value.replace(/[^\d\/]/g, '');

      if (cleaned.length > 10) {
        cleaned = cleaned.substring(0, 10);
      }

      const parts = cleaned.split('/');
      if (parts.length > 3) {
        cleaned = parts.slice(0, 3).join('/');
      }

      const finalParts = cleaned.split('/');
      let result = '';

      for (let i = 0; i < finalParts.length; i++) {
        const part = finalParts[i];

        if (i === 0) {
          result += part.substring(0, 2);
        } else if (i === 1) {
          result += '/' + part.substring(0, 2);
        } else if (i === 2) {
          result += '/' + part.substring(0, 4);
        }
      }

      return result;
    },

    validateAndEmit(value) {
      if (!value) {
        this.isInvalid = false;
        this.errorMessage = "";
        this.datepickerValue = "";
        this.$emit("input", "");
        return;
      }

      const isComplete = /^\d{2}\/\d{2}\/\d{4}$/.test(value);

      if (isComplete) {
        const validation = this.validateCompleteDate(value);

        if (validation.valid) {
          this.isInvalid = false;
          this.errorMessage = "";
          const isoDate = this.formatDateISO(value);
          this.datepickerValue = isoDate;
          this.$emit("input", isoDate);
        } else {
          this.isInvalid = true;
          this.errorMessage = validation.message;
          this.$emit("input", "");
        }
      } else {
        this.isInvalid = false;
        this.errorMessage = "";
        this.$emit("input", "");
      }
    },

    validateCompleteDate(dateStr) {
      const parts = dateStr.split("/");

      if (parts.length !== 3) {
        return { valid: false, message: "Format invalide. Utilisez JJ/MM/AAAA" };
      }

      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);

      if (isNaN(day) || isNaN(month) || isNaN(year)) {
        return { valid: false, message: "Format invalide. Utilisez JJ/MM/AAAA" };
      }

      if (month < 1 || month > 12) {
        return { valid: false, message: "Le mois doit être compris entre 01 et 12" };
      }

      if (day < 1 || day > 31) {
        return { valid: false, message: "Le jour doit être compris entre 01 et 31" };
      }

      if (year < 1900) {
        return { valid: false, message: "L'année ne peut pas être antérieure à 1900" };
      }

      if (year > 2100) {
        return { valid: false, message: "L'année ne peut pas être postérieure à 2100" };
      }

      const date = new Date(year, month - 1, day);
      const isValidDate = date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day;

      if (!isValidDate) {
        if (day > 28 && month === 2) {
          const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
          const maxDays = isLeapYear ? 29 : 28;
          return { valid: false, message: `Février ${year} ne peut avoir que ${maxDays} jours maximum` };
        }

        const daysInMonth = new Date(year, month, 0).getDate();
        if (day > daysInMonth) {
          const monthNames = [
            'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
            'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'
          ];
          return { valid: false, message: `${monthNames[month - 1]} ${year} ne peut avoir que ${daysInMonth} jours maximum` };
        }

        return { valid: false, message: "Cette date n'existe pas" };
      }

      return { valid: true };
    },

    onContext(ctx) {
      if (ctx.selectedYMD) {
        this.isInternalUpdate = true;
        const formattedDate = this.formatDateFR(ctx.selectedYMD);
        this.localValue = formattedDate;
        this.previousValue = formattedDate;
        this.datepickerValue = ctx.selectedYMD;
        this.$emit("input", ctx.selectedYMD);
        this.isInvalid = false;
        this.errorMessage = "";
        this.$nextTick(() => {
          this.isInternalUpdate = false;
        });
      }
      this.$emit('change');
    },

    formatDateFR(dateStr) {
      if (!dateStr) return "";

      const date = dateStr instanceof Date ? dateStr : new Date(dateStr);

      if (isNaN(date.getTime())) {
        return "";
      }

      const jour = String(date.getDate()).padStart(2, '0');
      const mois = String(date.getMonth() + 1).padStart(2, '0');
      const annee = date.getFullYear();

      return `${jour}/${mois}/${annee}`;
    },

    formatDateISO(dateStr) {
      if (!dateStr) return "";
      const parts = dateStr.split("/");
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
  }
};
</script>

<style scoped>
.datepicker-append {
  display: flex;
  align-items: center;
  height: calc(1.5em + 0.75rem + 2px)!important;
}

.is-invalid-append {
  border-color: #dc3545;
  background-repeat: no-repeat;
  background-position: right calc(0.375em + 0.1875rem) center;
  background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
}

.form-control {
  font-size: 0.875rem !important;
}
</style>



// WEBPACK FOOTER //
// src/components/b122/editFields/DatepickerInput.vue